import { cn } from "@/lib/utils";
import { getPassMin, usePassMin } from "@/lib/atsScores";

const KEYS = ["pass_rate", "ats_pass_rate", "ats_score", "pass_probability", "probability", "percentage", "percent", "score", "match_score"];
const LABEL_RE = /\b(pass|score|rate|probability|likelihood|percent(age)?)\b/i;

const toPct = (v) => {
  if (typeof v === "number" && isFinite(v)) return v > 0 && v <= 1 ? v * 100 : v;
  if (typeof v === "string") { const m = v.match(/(\d{1,3}(?:\.\d+)?)\s*%/) || v.match(/^\s*(\d{1,3}(?:\.\d+)?)\s*(?:\/\s*100)?\s*$/); if (m) return Number(m[1]); }
  return null;
};
const valid = (n) => n != null && n >= 0 && n <= 100;
const fromKeys = (o) => { for (const k of KEYS) { const n = toPct(o?.[k]); if (valid(n)) return n; } return null; };

export const extractScore = (res, allowText) => {
  if (!res || typeof res !== "object") return null;
  const top = fromKeys(res) ?? fromKeys(res.result);
  if (valid(top)) return top;
  const sections = res.sections || res.result?.sections || [];
  for (const s of sections) {
    if (!s || typeof s !== "object") continue;
    const k = fromKeys(s);
    if (valid(k)) return k;
    const head = s.heading || s.title || s.label || "";
    if (allowText && LABEL_RE.test(head)) { const n = toPct(String(s.body ?? s.content ?? s.text ?? s.value ?? "")); if (valid(n)) return n; }
  }
  if (allowText) { const m = JSON.stringify(res).match(/(\d{1,3}(?:\.\d+)?)\s*%/); if (m && valid(Number(m[1]))) return Number(m[1]); }
  return null;
};

export const band = (s, min = getPassMin()) =>
  s >= min ? { c: "#10B981", label: "Likely to pass", text: "text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/40" }
  : s >= min - 20 ? { c: "#F59E0B", label: "Borderline", text: "text-amber-400", bg: "bg-amber-500/10 border-amber-500/40" }
  : { c: "#EF4444", label: "Likely filtered out", text: "text-red-400", bg: "bg-red-500/10 border-red-500/40" };

export const ResultScore = ({ score, title = "ATS pass rate" }) => {
  const s = Math.round(score);
  const b = band(s, usePassMin());
  const r = 34, c = 2 * Math.PI * r;
  return (
    <div className={cn("fade-up flex items-center gap-5 rounded-2xl border p-5", b.bg)} data-testid="result-score-banner">
      <div className="relative h-24 w-24 shrink-0">
        <svg viewBox="0 0 80 80" className="h-24 w-24 -rotate-90">
          <circle cx="40" cy="40" r={r} stroke="#27272A" strokeWidth="7" fill="none" />
          <circle cx="40" cy="40" r={r} stroke={b.c} strokeWidth="7" fill="none" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (c * s) / 100} style={{ transition: "stroke-dashoffset 0.9s ease" }} />
        </svg>
        <span className="absolute inset-0 flex items-center justify-center font-display text-2xl font-semibold text-zinc-100" data-testid="result-score-value">{s}%</span>
      </div>
      <div className="min-w-0">
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500">{title}</div>
        <div className={cn("mt-1 flex items-center gap-2 font-display text-xl font-semibold", b.text)} data-testid="result-score-label">
          <span className="h-2.5 w-2.5 rounded-full" style={{ background: b.c }} />{b.label}
        </div>
        <div className="mt-2 h-1.5 w-48 max-w-full overflow-hidden rounded-full bg-[#27272A]"><div className="h-full rounded-full" style={{ width: `${s}%`, background: b.c }} /></div>
      </div>
    </div>
  );
};
